/*
PURPOSE: React text rendering utility that combines URL auto-linking and mentions with React elements

USE CASES:
- Convert plain text with URLs into React elements with clickable links
- Convert @mentions into clickable user links
- Process comment text for display in CommentCard components
- Maintain text formatting while adding link and mention functionality
- Support mixed content (text + URLs + mentions) rendering

DOMAIN BOUNDARIES:
- Responsible for React element generation only
- Bridges urlProcessor and mentionProcessor utilities with React components
- Does NOT handle text storage or persistence
- UI rendering focused on comment display

ASSUMPTIONS:
- Input text may contain URLs and @mentions that need to be clickable
- Rendered links should use AutoLink component styling
- Rendered mentions should use UserMention component styling
- Non-special text should render as React text nodes
- Component should be performant for comment feeds
- Mentions are processed before URLs to avoid conflicts
*/

import React from 'react';
import { processTextWithURLs, TextSegment } from './urlProcessor';
import { processTextWithMentions, MentionInfo } from './mentionProcessor';
import AutoLink from '../components/AutoLink/AutoLink';
import UserMention from '../components/UserMention/UserMention';
import MentionBubble from '../components/UserMention/MentionBubble';
import { MentionMetadata } from 'shared/types/contentTypes';

/**
 * Combined segment type for mixed content
 */
interface CombinedSegment {
  text: string;
  type: 'text' | 'url' | 'mention';
  urlInfo?: import('./urlProcessor').URLInfo;
  mentionInfo?: MentionInfo;
}

/**
 * Combines mention and URL segments into a unified array
 * Mentions are processed first to take precedence
 */
function combineSegments(
  text: string,
  mentions: MentionInfo[]
): CombinedSegment[] {
  const combinedSegments: CombinedSegment[] = [];

  // First, process mentions
  const mentionSegments = processTextWithMentions(text, mentions);

  // For each mention segment, check if it's text and process for URLs
  mentionSegments.forEach(segment => {
    if (segment.isMention && segment.mentionInfo) {
      // This is a mention - add it as-is
      combinedSegments.push({
        text: segment.text,
        type: 'mention',
        mentionInfo: segment.mentionInfo
      });
    } else {
      // This is plain text - check for URLs within it
      const urlSegments = processTextWithURLs(segment.text);

      urlSegments.forEach(urlSeg => {
        if (urlSeg.isURL && urlSeg.urlInfo) {
          combinedSegments.push({
            text: urlSeg.text,
            type: 'url',
            urlInfo: urlSeg.urlInfo
          });
        } else {
          combinedSegments.push({
            text: urlSeg.text,
            type: 'text'
          });
        }
      });
    }
  });

  return combinedSegments;
}

/**
 * Renders text with URLs as clickable links using React elements
 * @param text - The text content that may contain URLs
 * @returns React elements with clickable links for URLs
 */
export function renderTextWithLinks(text: string): React.ReactNode {
  if (!text || typeof text !== 'string') {
    return null;
  }

  const segments = processTextWithURLs(text);

  // If no URLs found, return plain text
  if (segments.length === 1 && !segments[0].isURL) {
    return text;
  }

  // Map segments to React elements
  return segments.map((segment: TextSegment, index: number) => {
    if (segment.isURL && segment.urlInfo) {
      return (
        <AutoLink
          key={`url-${index}`}
          urlInfo={segment.urlInfo}
        >
          {segment.text}
        </AutoLink>
      );
    }

    // Regular text segment
    return (
      <React.Fragment key={`text-${index}`}>
        {segment.text}
      </React.Fragment>
    );
  });
}

/**
 * Renders text with both @mentions and URLs as clickable elements
 * @param text - The text content that may contain mentions and URLs
 * @param mentions - Optional pre-resolved mention information
 * @returns React elements with clickable mentions and links
 */
export function renderTextWithLinksAndMentions(
  text: string,
  mentions: MentionInfo[] = []
): React.ReactNode {
  if (!text || typeof text !== 'string') {
    return null;
  }

  const segments = combineSegments(text, mentions);

  // If only one plain text segment, return as-is
  if (segments.length === 1 && segments[0].type === 'text') {
    return text;
  }

  // Map segments to React elements
  return segments.map((segment: CombinedSegment, index: number) => {
    if (segment.type === 'mention' && segment.mentionInfo) {
      return (
        <UserMention
          key={`mention-${index}`}
          mentionInfo={segment.mentionInfo}
        >
          {segment.text}
        </UserMention>
      );
    }

    if (segment.type === 'url' && segment.urlInfo) {
      return (
        <AutoLink
          key={`url-${index}`}
          urlInfo={segment.urlInfo}
        >
          {segment.text}
        </AutoLink>
      );
    }

    // Regular text segment
    return (
      <React.Fragment key={`text-${index}`}>
        {segment.text}
      </React.Fragment>
    );
  });
}

/**
 * Renders comment with mentions (using stored metadata) and URLs
 * This is the PRIMARY function for rendering comments with @ mentions
 * Uses position-based metadata instead of regex detection
 *
 * @param text - Comment text
 * @param mentionMetadata - Stored mention metadata with positions
 * @returns React elements with Slack-style mention bubbles and clickable URLs
 */
export function renderCommentWithMentions(
  text: string,
  mentionMetadata?: MentionMetadata[]
): React.ReactNode {
  if (!text || typeof text !== 'string') {
    return null;
  }

  // If no metadata, use URL-only rendering (backward compatibility)
  if (!mentionMetadata || mentionMetadata.length === 0) {
    return renderTextWithLinks(text);
  }

  // Sort mentions by startIndex to process in order
  const sortedMentions = [...mentionMetadata].sort((a, b) => a.startIndex - b.startIndex);

  const segments: CombinedSegment[] = [];
  let lastIndex = 0;

  for (const mention of sortedMentions) {
    // Text before mention - process for URLs
    if (mention.startIndex > lastIndex) {
      const textBefore = text.slice(lastIndex, mention.startIndex);
      const urlSegments = processTextWithURLs(textBefore);

      urlSegments.forEach(seg => {
        segments.push({
          text: seg.text,
          type: seg.isURL ? 'url' : 'text',
          urlInfo: seg.urlInfo
        });
      });
    }

    // Mention segment - add with metadata
    segments.push({
      text: text.slice(mention.startIndex, mention.endIndex),
      type: 'mention',
      mentionInfo: {
        username: mention.displayName,
        userId: mention.userId,
        displayName: mention.displayName,
        startIndex: mention.startIndex,
        endIndex: mention.endIndex,
        isValid: true
      }
    });

    lastIndex = mention.endIndex;
  }

  // Remaining text - process for URLs
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    const urlSegments = processTextWithURLs(remainingText);

    urlSegments.forEach(seg => {
      segments.push({
        text: seg.text,
        type: seg.isURL ? 'url' : 'text',
        urlInfo: seg.urlInfo
      });
    });
  }

  // Render segments with MentionBubble for mentions
  return segments.map((segment: CombinedSegment, index: number) => {
    if (segment.type === 'mention' && segment.mentionInfo) {
      return (
        <MentionBubble
          key={`mention-${index}`}
          userId={segment.mentionInfo.userId!}
          displayName={segment.mentionInfo.displayName!}
        >
          {segment.text}
        </MentionBubble>
      );
    }

    if (segment.type === 'url' && segment.urlInfo) {
      return (
        <AutoLink
          key={`url-${index}`}
          urlInfo={segment.urlInfo}
        >
          {segment.text}
        </AutoLink>
      );
    }

    return (
      <React.Fragment key={`text-${index}`}>
        {segment.text}
      </React.Fragment>
    );
  });
}

/**
 * Alternative function that returns URL processing information
 * Useful for components that need more control over rendering
 */
export function processCommentText(text: string) {
  return {
    segments: processTextWithURLs(text),
    hasUrls: processTextWithURLs(text).some(segment => segment.isURL),
    render: () => renderTextWithLinks(text)
  };
}

/**
 * Renders text with markdown-style *italics* converted to <em> tags
 * @param text - The text content that may contain *italic* markers
 * @returns React elements with italic text properly rendered
 */
export function renderTextWithItalics(text: string): React.ReactNode {
  if (!text || typeof text !== 'string') {
    return null;
  }

  // Split text by *text* pattern
  const parts: React.ReactNode[] = [];
  const regex = /\*([^*]+)\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    // Add italic text
    parts.push(<em key={`italic-${match.index}`}>{match[1]}</em>);
    
    lastIndex = regex.lastIndex;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // If no matches found, return original text
  if (parts.length === 0) {
    return text;
  }

  return <>{parts}</>;
}